/**
 * Path patterns in Hono syntax: literal segments, `:name` parameters and an optional trailing
 * `*` wildcard. The wildcard's value is carried as the parameter named `*`.
 */
const LITERAL_SEGMENT = /^[A-Za-z0-9._~-]+$/;
const PARAM_SEGMENT = /^:([a-zA-Z][a-zA-Z0-9]*)$/;
export const WILDCARD = '*';
function segmentsOf(path) {
    return path.split('/').slice(1);
}
/** True for `/a/:b/c` and `/a/:b/*`; false for a relative, empty-segment or odd pattern. */
export function isValidPathPattern(path) {
    if (!path.startsWith('/') || path === '/')
        return path === '/';
    const segments = segmentsOf(path);
    const names = new Set();
    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        if (segment === WILDCARD) {
            if (i !== segments.length - 1)
                return false;
            continue;
        }
        const param = PARAM_SEGMENT.exec(segment);
        if (param) {
            const name = param[1];
            if (names.has(name))
                return false;
            names.add(name);
            continue;
        }
        if (!LITERAL_SEGMENT.test(segment))
            return false;
    }
    return true;
}
/** Names of the `:param` segments, in order, followed by `*` when the path has a wildcard. */
export function pathParamNames(path) {
    const names = [];
    for (const segment of segmentsOf(path)) {
        const param = PARAM_SEGMENT.exec(segment);
        if (param)
            names.push(param[1]);
        else if (segment === WILDCARD)
            names.push(WILDCARD);
    }
    return names;
}
/** `/api/docs/:id` and `/api/docs/:docId` are the same route to the router. */
export function routeShape(method, path) {
    const shape = segmentsOf(path)
        .map((segment) => (PARAM_SEGMENT.test(segment) ? ':' : segment))
        .join('/');
    return `${method} /${shape}`;
}
/**
 * Fill a pattern from `params`. Named values are URI-encoded as one segment; the wildcard value
 * keeps its slashes and each of its segments is encoded. A missing value throws.
 */
export function buildPath(pattern, params = {}) {
    const out = segmentsOf(pattern).map((segment) => {
        const param = PARAM_SEGMENT.exec(segment);
        const name = param ? param[1] : segment === WILDCARD ? WILDCARD : undefined;
        if (name === undefined)
            return segment;
        const value = params[name];
        if (value === undefined || value === null) {
            throw new TypeError(`missing path parameter "${name}" for ${pattern}`);
        }
        const text = String(value);
        if (name === WILDCARD)
            return text.split('/').map(encodeURIComponent).join('/');
        if (text === '')
            throw new TypeError(`empty path parameter "${name}" for ${pattern}`);
        return encodeURIComponent(text);
    });
    return `/${out.join('/')}`;
}
/**
 * Match a request pathname against a pattern. Named values are URI-decoded; the wildcard value is
 * returned raw (still percent-encoded) so a handler can refuse encoded traversal itself.
 * Returns null when the path does not match or a value cannot be decoded.
 */
export function matchPath(pattern, pathname) {
    const expected = segmentsOf(pattern);
    const actual = segmentsOf(pathname);
    const params = {};
    for (let i = 0; i < expected.length; i++) {
        const segment = expected[i];
        if (segment === WILDCARD) {
            params[WILDCARD] = actual.slice(i).join('/');
            return params;
        }
        const value = actual[i];
        if (value === undefined)
            return null;
        const param = PARAM_SEGMENT.exec(segment);
        if (param) {
            if (value === '')
                return null;
            try {
                params[param[1]] = decodeURIComponent(value);
            }
            catch {
                return null;
            }
        }
        else if (segment !== value) {
            return null;
        }
    }
    return actual.length === expected.length ? params : null;
}
/** `?a=1&b=x` from a flat query object; arrays repeat the key; undefined values are skipped. */
export function buildQueryString(query) {
    if (query === undefined || query === null)
        return '';
    if (typeof query !== 'object' || Array.isArray(query)) {
        throw new TypeError('query must be a plain object');
    }
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined)
            continue;
        if (Array.isArray(value)) {
            for (const item of value)
                search.append(key, String(item));
        }
        else {
            search.append(key, String(value));
        }
    }
    const text = search.toString();
    return text === '' ? '' : `?${text}`;
}
//# sourceMappingURL=path.js.map